/**
 * Where the development panel sits, how big it is, and whether it is rolled up.
 *
 * The geometry is arithmetic with no browser in it, so the rules that matter — the panel
 * can never be dragged somewhere it cannot be grabbed again, and it can never be resized
 * to nothing — are covered by ordinary tests rather than by pointing a browser at it.
 */

export type DevelopmentPanelGeometry = {
  left: number
  top: number
  width: number
  height: number
  isMinimized: boolean
}

export type ViewportSize = { width: number; height: number }

export const MINIMUM_PANEL_WIDTH = 260
export const MINIMUM_PANEL_HEIGHT = 140

/** How much of the panel must stay on screen, which is the header and a corner of it. */
const ALWAYS_VISIBLE_EDGE = 48
const DISTANCE_FROM_VIEWPORT_EDGE = 16
const DEFAULT_PANEL_WIDTH = 340
const DEFAULT_PANEL_HEIGHT = 320

const PANEL_STORAGE_KEY = 'threadline.developmentToolsPanel'

function clampToRange(value: number, lowestValue: number, highestValue: number): number {
  // A viewport smaller than the panel would invert the range, and the lowest value is the
  // safe end of it: the header stays reachable even on a tiny window.
  return Math.max(lowestValue, Math.min(highestValue, value))
}

export function buildDefaultPanelGeometry(viewport: ViewportSize): DevelopmentPanelGeometry {
  const width = Math.min(DEFAULT_PANEL_WIDTH, viewport.width)
  const height = Math.min(DEFAULT_PANEL_HEIGHT, viewport.height)
  return {
    left: viewport.width - width - DISTANCE_FROM_VIEWPORT_EDGE,
    top: viewport.height - height - DISTANCE_FROM_VIEWPORT_EDGE,
    width,
    height,
    isMinimized: false,
  }
}

/**
 * Brings a geometry back inside the window: never narrower than it can be read, never
 * taller than the window, and never so far out that the header cannot be grabbed. A
 * window that was resized between two visits goes through here as well, which is why a
 * panel parked in a corner does not disappear when the window shrinks.
 */
export function clampPanelGeometry(
  geometry: DevelopmentPanelGeometry,
  viewport: ViewportSize,
): DevelopmentPanelGeometry {
  const width = clampToRange(geometry.width, MINIMUM_PANEL_WIDTH, viewport.width)
  const height = clampToRange(geometry.height, MINIMUM_PANEL_HEIGHT, viewport.height)

  return {
    width,
    height,
    // Dragging mostly off the side is allowed and useful; losing the panel is not.
    left: clampToRange(
      geometry.left,
      ALWAYS_VISIBLE_EDGE - width,
      viewport.width - ALWAYS_VISIBLE_EDGE,
    ),
    // The top edge is where the panel is dragged from, so it never goes above the window.
    top: clampToRange(geometry.top, 0, viewport.height - ALWAYS_VISIBLE_EDGE),
    isMinimized: geometry.isMinimized,
  }
}

export function readPanelGeometryFromStorage(): DevelopmentPanelGeometry | null {
  try {
    const storedGeometry = window.localStorage.getItem(PANEL_STORAGE_KEY)
    if (storedGeometry === null) {
      return null
    }
    const parsedGeometry = JSON.parse(storedGeometry) as Partial<DevelopmentPanelGeometry>
    if (
      typeof parsedGeometry.left !== 'number' ||
      typeof parsedGeometry.top !== 'number' ||
      typeof parsedGeometry.width !== 'number' ||
      typeof parsedGeometry.height !== 'number'
    ) {
      return null
    }
    return {
      left: parsedGeometry.left,
      top: parsedGeometry.top,
      width: parsedGeometry.width,
      height: parsedGeometry.height,
      isMinimized: parsedGeometry.isMinimized === true,
    }
  } catch {
    // Storage that is disabled, or a value somebody edited by hand, simply means the
    // panel opens where it opens the first time.
    return null
  }
}

export function persistPanelGeometry(geometry: DevelopmentPanelGeometry): void {
  try {
    window.localStorage.setItem(PANEL_STORAGE_KEY, JSON.stringify(geometry))
  } catch {
    // Nothing is lost but the memory of where it was left.
  }
}

/** The geometry to open with: whatever was left behind, pulled back into this window. */
export function resolveInitialPanelGeometry(viewport: ViewportSize): DevelopmentPanelGeometry {
  const storedGeometry = readPanelGeometryFromStorage()
  return clampPanelGeometry(storedGeometry ?? buildDefaultPanelGeometry(viewport), viewport)
}
